import React, { useEffect, useState } from "react";
import flat from "flat";
import { Center, Stepper } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useLocation } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { showNotification } from "@mantine/notifications";
import { loadingStates } from "../../utilities/utilities";
import colors, { themeColor } from "../../utilities/design";
import LegacyAgreementForm1 from "./LegacyAgreementForm1.jsx";
import {
  createAgreement,
  getAgreementDetails,
  getAgreementTitleApi,
  getAgreementTypesApi,
  updateAgreement,
} from "../../utilities/apis/agreements";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import LegacyAgreementForm3 from "./LegacyAgreementForm3.jsx";
import LegacyAgreementForm4 from "./LegacyAgreementForm4.jsx";
import {
  addDays,
  parseISO,
  subDays,
  differenceInDays,
  addYears,
  addMonths,
} from "date-fns";

const LEGACY_PARTY_KEYS = [
  "firstParty",
  "secondParty",
  "thirdParty",
  "fourthParty",
  "fifthParty",
  "sixthParty",
  "seventhParty",
  "eighthParty",
  "ninthParty",
  "tenthParty",
  "eleventhParty",
  "twelthParty",
  "thirteenthParty",
  "fourteenthParty",
  "fifteenthParty",
  "sixteenthParty",
  "seventeenthParty",
  "eighteenthParty",
  "nineteenthParty",
  "twentythParty",
];

const convertLegacyResponseToFormValues = (agreement) => {
  const json = agreement.agreementData?.json || {};
  const partyValues = LEGACY_PARTY_KEYS.reduce((acc, key) => {
    const fromJson = json[key] && typeof json[key] === "object" ? json[key] : {};
    const fromAgreement =
      agreement[key] && typeof agreement[key] === "object" ? agreement[key] : {};
    acc[key] = { ...fromJson, ...fromAgreement };
    return acc;
  }, {});

  return {
    typeOfAgreement: agreement.typeOfAgreement,
    titleOfAgreement: agreement.titleOfAgreement,
    requestingUser: agreement.requestingUserDetails || {},
    dateOfContract: agreement.dateOfContract || null,
    dueDateOfContract: agreement.dueDateOfContract || json.dueDateOfContract || null,
    location: agreement.location || json.location || null,
    contractQuillJsDelta: agreement.contractQuillJsDelta || null,
    formCompleted: !!agreement.formCompleted,
    noOfMonths: agreement.noOfMonths ?? json.noOfMonths ?? null,
    noOfYears: agreement.noOfYears ?? json.noOfYears ?? "",
    noOfDays:
      json.noOfDays ??
      (agreement.noOfDate != null ? String(agreement.noOfDate) : "0"),
    reminderTimeInDays:
      agreement.reminderTimeInDays ?? json.reminderTimeInDays ?? 15,
    relationshipType: agreement.relationshipType || "",
    relationshipWith: agreement.relationshipWith || "",
    jobOrder: agreement.jobOrder || json.jobOrder || "",
    businessUnit: json.businessUnit || "1",
    status: agreement.status || json.status || "SIGNED",
    isLegacy: true,
    year: json.year ?? 2024,
    month: json.month ?? "Jan",
    fileSelected: !!agreement.formCompleted,
    ...partyValues,
  };
};

const CreateNewLegacyAgreement = () => {
  const { state } = useLocation();
  const isEditMode = Boolean(state?.edit && state?.agreementId);
  const FORM_STEPS = {
    STEP_1: 0,
    STEP_2: 1,
    STEP_3: 2,
  };

  const [uiConfigs, setUiConfigs] = useState({
    currentStep: FORM_STEPS.STEP_1,
    loading: loadingStates.NO_ACTIVE_REQUEST,
  });

  const [legacyAgreementFormValue, setLegacyAgreementFormValue] = useState({
    typeOfAgreementData: [],
    typeOfAgreement: null,
    titleOfAgreementData: [],
    titleOfAgreement: null,
    relationshipType: "",
    relationshipWith: "",
    jobOrder: "",
    requestingUser: {
      name: "",
      email: "",
      phone: "",
      designation: "",
      businessDepartment: "",
    },
    contractQuillJsDelta: null,
    dateOfContract: null,
    noOfMonths: null,
    noOfYears: "",
    reminderTimeInDays: 15,
    isLegacy: true,
    firstParty: {},
    secondParty: {},
    thirdParty: {},
    fourthParty: {},
    fifthParty: {},
    sixthParty: {},
    seventhParty: {},
    eighthParty: {},
    ninthParty: {},
    tenthParty: {},
    eleventhParty: {},
    twelthParty: {},
    thirteenthParty: {},
    fourteenthParty: {},
    fifteenthParty: {},
    sixteenthParty: {},
    seventeenthParty: {},
    eighteenthParty: {},
    nineteenthParty: {},
    twentythParty: {},
    documents: [],
    fileSelected: false,
    formCompleted: false,
    location: null,
    businessUnit: "1",
    status: "SIGNED",
    year: 2024,
    month: "Jan",
  });

  const getAgreementTypes = async () => {
    const resp = await apiWrapWithErrorWithData(getAgreementTypesApi());
    if (resp?.success && resp?.typesOfAgreement) {
      const uniqueValues = Array.from(new Set([...resp.typesOfAgreement]));
      setLegacyAgreementFormValue((prevState) => ({
        ...prevState,
        typeOfAgreementData: uniqueValues,
      }));
    } else {
      // showNotification({
      //   title: 'Agreement Types',
      //   message: 'Could not load agreement types',
      //   color: 'red',
      // });
    }
  };
  const getAgreementTitle = async () => {
    const resp = await apiWrapWithErrorWithData(getAgreementTitleApi());
    if (resp?.success && resp?.titlesOfAgreement) {
      const uniqueValues = Array.from(new Set([...resp.titlesOfAgreement]));
      setLegacyAgreementFormValue((prevState) => ({
        ...prevState,
        titleOfAgreementData: uniqueValues,
      }));
    } else {
      // showNotification({
      //   title: 'Agreement Title',
      //   message: 'Could not load agreement titles',
      //   color: 'red',
      // });
    }
  };
  useEffect(() => {
    getAgreementTitle();
    getAgreementTypes();
  }, []);
  const [agreementId, setAgreementId] = useState(state?.agreementId || null);
  const [agreementLoaded, setAgreementLoaded] = useState(!isEditMode);
  const agreementForm = useForm({
    initialValues: flat(legacyAgreementFormValue),
  });

  const fetchAgreementData = async (incomingAgreementId) => {
    setUiConfigs((prev) => ({
      ...prev,
      loading: loadingStates.LOADING,
    }));

    const response = await apiWrapWithErrorWithData(
      getAgreementDetails({ agreementId: incomingAgreementId }),
    );

    if (response?.success && response.agreement) {
      const formValues = convertLegacyResponseToFormValues(response.agreement);
      setLegacyAgreementFormValue((prev) => ({
        ...prev,
        ...formValues,
      }));
      agreementForm.setValues(flat(formValues));
      setAgreementId(incomingAgreementId);
      setAgreementLoaded(true);
    } else {
      showNotification({
        color: "red",
        title: "Agreement",
        message: "Could not load agreement details.",
      });
      setAgreementLoaded(true);
    }

    setUiConfigs((prev) => ({
      ...prev,
      loading: loadingStates.NO_ACTIVE_REQUEST,
    }));
  };

  useEffect(() => {
    if (isEditMode && state?.agreementId && !agreementLoaded) {
      fetchAgreementData(state.agreementId);
    }
  }, [isEditMode, state?.agreementId, agreementLoaded]);

  const saveAgreement = async (values) => {
    const activeValues = values || flat(legacyAgreementFormValue);
    const unflat = flat.unflatten(activeValues);
    const agreementData = {
      json: unflat,
      flatted: activeValues,
    };
    const toUse = agreementId ? updateAgreement : createAgreement;

    const startDate = parseISO(legacyAgreementFormValue.dateOfContract);

    // Build end date (calendar-correct)
    const endDate = legacyAgreementFormValue.dueDateOfContract
      ? parseISO(legacyAgreementFormValue.dueDateOfContract)
      : subDays(
          addDays(
            addMonths(
              addYears(
                startDate,
                parseInt(legacyAgreementFormValue.noOfYears || 0, 10),
              ),
              parseInt(legacyAgreementFormValue.noOfMonths || 0, 10),
            ),
            parseInt(legacyAgreementFormValue.noOfDays || 0, 10),
          ),
          1,
        );

    const totalDays = differenceInDays(endDate, startDate);

    const agreementArgs = {
      typeOfAgreement: legacyAgreementFormValue.typeOfAgreement,
      titleOfAgreement: legacyAgreementFormValue.titleOfAgreement,
      requestingUserDetails: legacyAgreementFormValue.requestingUser,
      dateOfContract: legacyAgreementFormValue.dateOfContract,
      contractTermInDays: totalDays,
      noOfYears: parseInt(legacyAgreementFormValue.noOfYears, 10),
      noOfMonths: parseInt(legacyAgreementFormValue.noOfMonths, 10),
      noOfDays: parseInt(legacyAgreementFormValue.noOfDays || 0, 10),
      location: legacyAgreementFormValue.location,
      reminderTimeInDays: parseInt(
        legacyAgreementFormValue.reminderTimeInDays,
        10,
      ),
      // dueDateOfContract: contractDueDate,
      dueDateOfContract: endDate,
      reminderTime: subDays(
        endDate,
        parseInt(legacyAgreementFormValue.reminderTimeInDays || 0, 10),
      ),
      contractQuillJsDelta: legacyAgreementFormValue.contractQuillJsDelta,
      firstParty: unflat.firstParty || legacyAgreementFormValue.firstParty,
      secondParty: unflat.secondParty || legacyAgreementFormValue.secondParty,
      thirdParty: unflat.thirdParty || legacyAgreementFormValue.thirdParty,
      fourthParty: unflat.fourthParty || legacyAgreementFormValue.fourthParty,
      fifthParty: unflat.fifthParty || legacyAgreementFormValue.fifthParty,
      sixthParty: unflat.sixthParty || legacyAgreementFormValue.sixthParty,
      seventhParty: unflat.seventhParty || legacyAgreementFormValue.seventhParty,
      eighthParty: unflat.eighthParty || legacyAgreementFormValue.eighthParty,
      ninthParty: unflat.ninthParty || legacyAgreementFormValue.ninthParty,
      tenthParty: unflat.tenthParty || legacyAgreementFormValue.tenthParty,
      eleventhParty: unflat.eleventhParty || legacyAgreementFormValue.eleventhParty,
      twelthParty: unflat.twelthParty || legacyAgreementFormValue.twelthParty,
      thirteenthParty: unflat.thirteenthParty || legacyAgreementFormValue.thirteenthParty,
      fourteenthParty: unflat.fourteenthParty || legacyAgreementFormValue.fourteenthParty,
      fifteenthParty: unflat.fifteenthParty || legacyAgreementFormValue.fifteenthParty,
      sixteenthParty: unflat.sixteenthParty || legacyAgreementFormValue.sixteenthParty,
      seventeenthParty: unflat.seventeenthParty || legacyAgreementFormValue.seventeenthParty,
      eighteenthParty: unflat.eighteenthParty || legacyAgreementFormValue.eighteenthParty,
      nineteenthParty: unflat.nineteenthParty || legacyAgreementFormValue.nineteenthParty,
      twentythParty: unflat.twentythParty || legacyAgreementFormValue.twentythParty,
      formCompleted: unflat.formCompleted ?? legacyAgreementFormValue.formCompleted,
      relationshipType: legacyAgreementFormValue.relationshipType,
      relationshipWith: legacyAgreementFormValue.relationshipWith,
      jobOrder: legacyAgreementFormValue.jobOrder || "",
      businessUnit: legacyAgreementFormValue.businessUnit || "1",
      status: legacyAgreementFormValue.status || "SIGNED",
    };
    const args = agreementId
      ? {
          agreementId: agreementId,
          agreementData,
          ...agreementArgs,
        }
      : { agreementData, ...agreementArgs };

    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.LOADING,
    });
    const response = await apiWrapWithErrorWithData(toUse(args));
    if (response?.success) {
      if (response?.agreement?.id) {
        setAgreementId(response.agreement.id);
      }
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    } else {
      // showNotification({
      //   color: 'red',
      //   title: 'Agreement Form',
      //   message: 'Something went wrong.',
      // });
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });

      return false;
    }

    return true;
  };

  const allowStepSelect =
    isEditMode ||
    legacyAgreementFormValue.formCompleted ||
    Boolean(agreementId);

  if (isEditMode && !agreementLoaded) {
    return (
      <Center className="min-h-[40vh]">
        <BeatLoader color={colors.primary} />
      </Center>
    );
  }

  return (
    <div>
      <Stepper
        color={themeColor(colors.agreement)}
        active={uiConfigs.currentStep}
        onStepClick={(step) => {
          if (!allowStepSelect) return;
          setUiConfigs({
            ...uiConfigs,
            currentStep: step,
          });
        }}
        breakpoint="sm"
        style={{ paddingRight: "10px" }}
      >
        <Stepper.Step allowStepSelect={allowStepSelect} label="key details">
          <LegacyAgreementForm1
            saveAgreement={saveAgreement}
            setLegacyAgreementFormValue={setLegacyAgreementFormValue}
            legacyAgreementFormValue={legacyAgreementFormValue}
            uiConfigs={uiConfigs}
            setUiConfigs={setUiConfigs}
          />
        </Stepper.Step>

        <Stepper.Step allowStepSelect={allowStepSelect} label="parties">
          <LegacyAgreementForm3
            agreementId={agreementId}
            saveAgreement={saveAgreement}
            setLegacyAgreementFormValue={setLegacyAgreementFormValue}
            legacyAgreementFormValue={legacyAgreementFormValue}
            uiConfigs={uiConfigs}
            setUiConfigs={setUiConfigs}
          />
        </Stepper.Step>
        <Stepper.Step allowStepSelect={allowStepSelect} label="select agreement">
          <LegacyAgreementForm4
            saveAgreement={saveAgreement}
            uiConfigs={uiConfigs}
            agreementId={agreementId}
            setUiConfigs={setUiConfigs}
            legacyAgreementFormValue={legacyAgreementFormValue}
            setLegacyAgreementFormValue={setLegacyAgreementFormValue}
            isEdit={isEditMode}
          />
        </Stepper.Step>
      </Stepper>
    </div>
  );
};

export default CreateNewLegacyAgreement;
